import { z } from 'zod';

import { notifyUsageError } from '@/shared/api/usage-events';
import { env } from '@/shared/lib/env';
import { supabase } from '@/shared/lib/supabase';

import { microphonePcm, pcmBase64 } from './pcm.service';

const transcriptSchema = z.object({
  message_type: z.string(),
  text: z.string().optional(),
  error: z.unknown().optional(),
});

// Provider payloads can include account details. Surface only these fixed, actionable messages.
const providerErrors = new Map([
  [
    'usage_limit_exceeded',
    'Your voice allowance is used. Open Limits & plans to upgrade your trial or see when your monthly allowance renews. You can continue in chat while AI credits remain.',
  ],
  [
    'quota_exceeded',
    'ElevenLabs speech recognition credits are exhausted. Add credits or renew the ElevenLabs plan before reconnecting voice. You can continue in chat.',
  ],
  [
    'auth_error',
    'ElevenLabs speech recognition authorization failed. Check the API key and its permissions before reconnecting voice. You can continue in chat.',
  ],
  [
    'unaccepted_terms',
    'Accept the speech recognition terms in the ElevenLabs account before reconnecting voice. You can continue in chat.',
  ],
  [
    'rate_limited',
    'ElevenLabs speech recognition is rate limited. Wait a moment before reconnecting voice.',
  ],
  [
    'resource_exhausted',
    'ElevenLabs speech recognition is at capacity. Wait a moment before reconnecting voice.',
  ],
  [
    'session_time_limit_exceeded',
    'This speech recognition session reached its time limit. Reconnect voice to continue.',
  ],
  [
    'insufficient_audio_activity',
    'Speech recognition paused after too little audio activity. Reconnect voice to continue.',
  ],
  [
    'transcriber_error',
    'ElevenLabs speech recognition could not process the audio. Reconnect voice to try again.',
  ],
  [
    'input_error',
    'ElevenLabs rejected the microphone audio. Check the voice configuration before reconnecting.',
  ],
  [
    'invalid_request',
    'ElevenLabs rejected the speech recognition request. Check the voice configuration before reconnecting.',
  ],
  [
    'commit_throttled',
    'Speech recognition received transcript commits too quickly. Wait a moment before reconnecting voice.',
  ],
  [
    'queue_overflow',
    'Speech recognition could not keep up with microphone audio. Reconnect voice to try again.',
  ],
  [
    'chunk_size_exceeded',
    'Speech recognition rejected an oversized audio chunk. Check the voice configuration before reconnecting.',
  ],
  ['error', 'Speech recognition failed. Reconnect voice to try again.'],
]);

// Capture runs on the audio rendering thread; hidden-tab animation throttling does not drive it.
const CAPTURE_PROCESSOR = `
class MioCapture extends AudioWorkletProcessor {
  constructor() { super(); this.samples = new Float32Array(2048); this.offset = 0; }
  process(inputs) {
    const input = inputs[0]?.[0];
    if (input) for (const sample of input) {
      this.samples[this.offset++] = sample;
      if (this.offset === this.samples.length) {
        this.port.postMessage(this.samples);
        this.offset = 0;
      }
    }
    return true;
  }
}
registerProcessor('mio-capture', MioCapture);`;

export interface CaptureDiagnostics {
  microphone: string;
  rms: number;
  chunksSent: number;
}
interface ScribeCallbacks {
  onPartial: (text: string) => void;
  onCommit: (text: string) => void;
  onActivity: (speaking: boolean) => void;
  onCapture?: (capture: CaptureDiagnostics) => void;
  onError: (message: string) => void;
  onInterrupted: () => void;
}

/** Metered voice uses our authenticated proxy; legacy sessions use a single-use provider token. */
export class ScribeMicrophone {
  private static owner: ScribeMicrophone | null = null;
  private socket: WebSocket | null = null;
  private media: MediaStream | null = null;
  private context: AudioContext | null = null;
  private capture: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private silent: GainNode | null = null;
  private muted = false;
  private closed = false;
  private loudFrames = 0;
  private watchdog: ReturnType<typeof setInterval> | undefined;
  private trackMuteTimers = new Set<ReturnType<typeof setTimeout>>();
  private cancelStart: (() => void) | null = null;

  constructor(private callbacks: ScribeCallbacks) {}

  /** Call during the start button gesture, before awaiting the session token or permission. */
  async prepare() {
    if (this.context) return;
    if (typeof AudioContext === 'undefined')
      throw new Error('This browser does not support live audio. You can continue in chat.');
    // Native hardware rates avoid Bluetooth input/output rate conflicts. PCM is resampled below.
    const context = new AudioContext();
    this.context = context;
    await context.resume();
  }

  async start(token: string, deviceId?: string) {
    const previous = ScribeMicrophone.owner;
    if (previous && previous !== this) {
      previous.stop();
      previous.callbacks.onError('Another call started in this tab. Reconnect to continue here.');
    }
    ScribeMicrophone.owner = this;
    this.closed = false;
    this.loudFrames = 0;
    try {
      if (!navigator.mediaDevices?.getUserMedia)
        throw new Error(
          'Microphone access requires a supported browser and a secure HTTPS connection.',
        );
      await this.prepare();
      if (this.closed) return;
      const media = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        },
      });
      if (this.closed) {
        media.getTracks().forEach((track) => track.stop());
        return;
      }
      this.media = media;
      this.mute(this.muted);
      const context = this.context;
      if (!context?.audioWorklet)
        throw new Error(
          'This browser does not support live microphone streaming. You can continue in chat.',
        );
      const objectUrl = URL.createObjectURL(
        new Blob([CAPTURE_PROCESSOR], { type: 'text/javascript' }),
      );
      try {
        await context.audioWorklet.addModule(objectUrl);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
      if (this.closed) return;
      const proxy = token === 'mio_proxy_v1';
      let accessToken: string | undefined;
      if (proxy) {
        const { data, error } = await supabase.auth.getSession();
        if (this.closed) return;
        if (error || !data.session) throw new Error('Sign in again to reconnect voice.');
        accessToken = data.session.access_token;
      }
      const query = new URLSearchParams({
        model_id: 'scribe_v2_realtime',
        token,
        audio_format: 'pcm_16000',
        language_code: 'en',
        commit_strategy: 'vad',
        vad_silence_threshold_secs: '0.7',
        min_speech_duration_ms: '150',
      });
      const proxyUrl = new URL('/api/v1/voice/transcribe', env.VITE_API_URL);
      proxyUrl.protocol = proxyUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      const socket = new WebSocket(
        proxy ? proxyUrl.toString() : `wss://api.elevenlabs.io/v1/speech-to-text/realtime?${query}`,
      );
      if (proxy) {
        socket.onopen = () => {
          if (!this.closed) socket.send(JSON.stringify({ type: 'auth', token: accessToken }));
        };
      }
      this.socket = socket;
      let ready = false;
      let failed = false;
      let sessionStarted = false;
      let chunksSent = 0;
      let lastFrameAt = Date.now();
      let lastDiagnosticAt = -Infinity;
      const label = media.getAudioTracks()[0]?.label;
      const microphone = label?.trim() ? label : 'Selected microphone';
      let acceptReady: (() => void) | undefined;
      let rejectReady: ((cause: Error) => void) | undefined;
      const readiness = new Promise<void>((resolve, reject) => {
        acceptReady = resolve;
        rejectReady = reject;
      });
      // Graph construction can throw before the awaited readiness check below is reached.
      void readiness.catch(() => undefined);
      const fail = (message: string) => {
        if (this.closed || failed) return;
        failed = true;
        clearTimeout(timeout);
        // Preserve the provider failure instead of replacing it with stop's AbortError.
        this.cancelStart = null;
        this.stop();
        if (ready) this.callbacks.onError(message);
        else rejectReady?.(new Error(message));
      };
      const timeout = setTimeout(
        () => fail('Speech recognition did not become ready. Reconnect voice to try again.'),
        15_000,
      );
      const checkReady = () => {
        if (ready || !sessionStarted || !chunksSent) return;
        ready = true;
        clearTimeout(timeout);
        acceptReady?.();
      };
      this.cancelStart = () => {
        clearTimeout(timeout);
        rejectReady?.(new DOMException('Voice connection stopped.', 'AbortError'));
      };
      // Install protocol handlers immediately: session_started may arrive as soon as the socket opens.
      socket.onmessage = (event: MessageEvent<unknown>) => {
        if (typeof event.data !== 'string' || this.closed) return;
        try {
          const parsed = transcriptSchema.safeParse(JSON.parse(event.data) as unknown);
          if (!parsed.success) return;
          const message = parsed.data;
          if (message.message_type === 'session_started') {
            sessionStarted = true;
            checkReady();
          } else if (message.message_type === 'partial_transcript')
            this.callbacks.onPartial(message.text ?? '');
          else if (message.message_type === 'committed_transcript')
            this.callbacks.onCommit(message.text ?? '');
          else {
            notifyUsageError(message.message_type);
            const failure = providerErrors.get(message.message_type);
            if (failure) fail(failure);
            else if (
              message.message_type !== 'warning' &&
              message.error !== undefined &&
              message.error !== null &&
              message.error !== '' &&
              message.error !== false
            )
              fail('Speech recognition reported an error. Reconnect voice to try again.');
            // Warnings, timestamp copies, and future informational events are not failures.
          }
        } catch {
          fail('Speech recognition returned an unreadable event.');
        }
      };
      socket.onclose = () =>
        fail('Voice disconnected. Your conversation is saved; reconnect to continue.');
      socket.onerror = () => fail('Speech recognition lost its connection.');
      context.onstatechange = () => {
        if (this.closed || context.state === 'running') return;
        if (ready) this.callbacks.onInterrupted();
        else fail('Your browser paused microphone capture. Resume voice to try again.');
      };
      for (const track of media.getAudioTracks()) {
        let muteTimer: ReturnType<typeof setTimeout> | undefined;
        track.onended = () =>
          fail('The microphone disconnected. Select an available input and reconnect.');
        const clearMute = () => {
          clearTimeout(muteTimer);
          if (muteTimer) this.trackMuteTimers.delete(muteTimer);
        };
        track.onmute = () => {
          clearMute();
          // Bluetooth route negotiation can briefly mute a healthy track.
          muteTimer = setTimeout(() => {
            clearMute();
            fail('The microphone stopped delivering audio. Reconnect when it is available.');
          }, 2000);
          this.trackMuteTimers.add(muteTimer);
        };
        track.onunmute = clearMute;
      }
      const capture = new AudioWorkletNode(context, 'mio-capture');
      this.capture = capture;
      capture.onprocessorerror = () =>
        fail('Microphone capture stopped. Reconnect voice to try again.');
      capture.port.onmessageerror = () =>
        fail('Microphone audio could not be read. Reconnect voice to try again.');
      capture.port.onmessage = (event: MessageEvent<Float32Array>) => {
        if (this.closed) return;
        lastFrameAt = Date.now();
        if (socket.readyState !== WebSocket.OPEN || (proxy && !sessionStarted)) return;
        const samples = event.data;
        let energy = 0;
        for (const sample of samples) energy += sample * sample;
        const rms = this.muted ? 0 : Math.sqrt(energy / Math.max(1, samples.length));
        this.loudFrames = rms > 0.025 ? this.loudFrames + 1 : 0;
        this.callbacks.onActivity(this.loudFrames >= 2);
        if (socket.bufferedAmount > 256_000) {
          fail(
            'The connection cannot keep up with live audio. Reconnect when your network improves.',
          );
          return;
        }
        const pcm = microphonePcm(
          this.muted ? new Float32Array(samples.length) : samples,
          context.sampleRate,
        );
        socket.send(
          JSON.stringify({
            message_type: 'input_audio_chunk',
            audio_base_64: pcmBase64(pcm),
            sample_rate: 16_000,
          }),
        );
        chunksSent++;
        checkReady();
        if (Date.now() - lastDiagnosticAt >= 250) {
          lastDiagnosticAt = Date.now();
          this.callbacks.onCapture?.({ microphone, rms, chunksSent });
        }
      };
      this.source = context.createMediaStreamSource(media);
      this.silent = context.createGain();
      this.silent.gain.value = 0;
      this.source.connect(capture).connect(this.silent).connect(context.destination);
      this.watchdog = setInterval(() => {
        if (Date.now() - lastFrameAt >= 5000)
          fail(
            'No audio is arriving from the microphone. Check the selected input and reconnect voice.',
          );
      }, 1000);
      try {
        await readiness;
      } finally {
        clearTimeout(timeout);
        this.cancelStart = null;
      }
    } catch (cause) {
      this.stop();
      throw cause;
    }
  }

  mute(muted: boolean) {
    this.muted = muted;
    this.media?.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  }

  stop() {
    if (ScribeMicrophone.owner === this) ScribeMicrophone.owner = null;
    this.closed = true;
    clearInterval(this.watchdog);
    for (const timer of this.trackMuteTimers) clearTimeout(timer);
    this.trackMuteTimers.clear();
    this.cancelStart?.();
    this.cancelStart = null;
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close();
    }
    this.socket = null;
    this.source?.disconnect();
    this.source = null;
    this.capture?.disconnect();
    this.capture = null;
    this.silent?.disconnect();
    this.silent = null;
    this.media?.getTracks().forEach((track) => {
      track.onended = null;
      track.onmute = null;
      track.onunmute = null;
      track.stop();
    });
    this.media = null;
    if (this.context && this.context.state !== 'closed') void this.context.close();
    this.context = null;
  }
}
